import React, { useEffect, useMemo, useState } from 'react';
import { Box, FormHelperText, Typography } from '@mui/material';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import TwoWheelerRoundedIcon from '@mui/icons-material/TwoWheelerRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import WeekendRoundedIcon from '@mui/icons-material/WeekendRounded';

const DEFAULT_OPTION_ICONS = {
  'Car Insurance': <DirectionsCarRoundedIcon sx={{ fontSize: 24 }} />,
  'Motorcycle Insurance': <TwoWheelerRoundedIcon sx={{ fontSize: 24 }} />,
  'Building Insurance': <HomeRoundedIcon sx={{ fontSize: 24 }} />,
  'Home Contents Insurance': <WeekendRoundedIcon sx={{ fontSize: 24 }} />,
};

const ComboCategorySelectorComponent = ({
  name,
  question,
  value,
  onChange,
  setValidationState,
  registerValidation,
  isMobile,
}) => {
  const options = useMemo(
    () => (Array.isArray(question?.options) ? question.options : []),
    [question?.options]
  );
  const optionMetaMap = useMemo(() => {
    const metaList = Array.isArray(question?.optionMeta)
      ? question.optionMeta
      : [];

    return metaList.reduce((acc, meta) => {
      if (!meta?.value) return acc;
      acc[meta.value] = meta;
      return acc;
    }, {});
  }, [question?.optionMeta]);

  const [selectedValues, setSelectedValues] = useState(
    Array.isArray(value) ? value : []
  );
  const [error, setError] = useState('');

  useEffect(() => {
    setSelectedValues(Array.isArray(value) ? value : []);
  }, [value]);

  useEffect(() => {
    setValidationState?.(
      name,
      question?.validation?.required ? selectedValues.length > 0 : true
    );
  }, [
    name,
    question?.validation?.required,
    selectedValues,
    setValidationState,
  ]);

  const validate = (nextValues = selectedValues) => {
    const isValid = question?.validation?.required
      ? nextValues.length > 0
      : true;

    setValidationState?.(name, isValid);
    setError(
      isValid
        ? ''
        : question?.validation?.message || 'Please select at least one option'
    );

    return isValid;
  };

  useEffect(() => {
    registerValidation?.(name, () => validate(selectedValues));
  }, [name, registerValidation, selectedValues]);

  const handleToggle = (option) => {
    const nextValues = selectedValues.includes(option)
      ? selectedValues.filter((item) => item !== option)
      : [...selectedValues, option];

    setSelectedValues(nextValues);
    onChange?.(name, nextValues);
    validate(nextValues);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        px: isMobile ? 1 : 0,
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: isMobile ? 'grid' : 'flex',
          gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'none',
          justifyContent: 'center',
          justifyItems: isMobile ? 'center' : 'unset',
          rowGap: isMobile ? '24px' : 0,
          flexWrap: isMobile ? 'unset' : 'wrap',
          maxWidth: '720px',
        }}
      >
        {options.map((option) => {
          const isSelected = selectedValues.includes(option);
          const optionMeta = optionMetaMap[option] || {};
          const displayLabel = optionMeta.displayLabel || option;

          return (
            <Box
              key={option}
              onClick={() => handleToggle(option)}
              role="checkbox"
              aria-checked={isSelected}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleToggle(option);
                }
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                outline: 'none',
                minWidth: isMobile ? '110px' : '120px',
                '&:hover .combo-option-icon': {
                  backgroundColor: '#C7CA00',
                },
              }}
            >
              <Box
                className="combo-option-icon"
                sx={{
                  backgroundColor: isSelected ? '#C7CA00' : '#E3E6E8',
                  width: '48px',
                  height: '48px',
                  borderRadius: '20px',
                  p: '12px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#162F3B',
                  transition: 'all 0.2s ease-in-out',
                  overflow: 'hidden',
                }}
              >
                {optionMeta.iconUrl ? (
                  <Box
                    component="img"
                    src={optionMeta.iconUrl}
                    alt={`${displayLabel} icon`}
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'block',
                    }}
                  />
                ) : (
                  DEFAULT_OPTION_ICONS[option] || (
                    <HomeRoundedIcon sx={{ fontSize: 24 }} />
                  )
                )}
              </Box>

              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#162F3B',
                  textAlign: 'center',
                  letterSpacing: '-0.002em',
                  maxWidth: '120px',
                }}
              >
                {displayLabel}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {error && (
        <FormHelperText
          sx={{
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: isMobile ? '0.7rem' : '0.75rem',
            color: '#D32F2F',
            mt: 1,
          }}
        >
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

export default ComboCategorySelectorComponent;
